import { types, getEnv, flow, SnapshotOut, Instance } from 'mobx-state-tree';
import { IAppEnv } from '../appEnvironment';
import { IGroupedApiCtx, IOdds, ISBApiOperationResponse, IBannersList, IDetail, IGame, getLongTipNameRaw, getRenameTipDataFromCompetitorNames, getCompetitorNamesFromEventName, IRenameTipData, protoUtils, LiveEvents, getSBApiStringToDateConverter, OfferType, OddModel, OfferItemState } from 'eTip--common';
import { ISportsBettingApi } from '../api/api';
import { observable } from 'mobx';

export const BannerOddsExtDataPartial = types.model({  //see also IOddsExtData
    eventName: types.string,
    gameName: types.string,
    eventDate: types.Date,
    number: types.number,
    offerType: types.number //enum of OfferType
})

const BannerItemGTMDataLayerViewPromotion = types.model({
    type: types.string,
    index: types.number
});

const BannerItemGTMDataLayerViewItemList = types.model({
    type: types.string,
    index: types.number
});

const BannerItemGTMDataLayer = types.model({
    viewPromotion: types.maybeNull(BannerItemGTMDataLayerViewPromotion),
    viewItemList: types.maybeNull(BannerItemGTMDataLayerViewItemList)
});

export const bannerItemModel = types.model({
    index: types.number,
    imgUrl: types.maybeNull(types.string),
    imgWebPUrl: types.maybeNull(types.string),
    imgWidth: types.number,
    imgHeight: types.number,
    linkUrl: types.maybeNull(types.string),
    label: types.string,
    oddsEventId: types.maybeNull(types.number),
    canBeLive: types.boolean, // it's reasonable to be subscribed for live data
    oddsExtData: types.maybeNull(BannerOddsExtDataPartial),
    odds: types.maybeNull(types.array(OddModel)),
    loadedImgUrl: types.maybeNull(types.string),
    loadedImgWebPUrl: types.maybeNull(types.string),
    isFinalImgLoaded: types.optional(types.boolean, false),
    gTMDataLayer: types.maybeNull(BannerItemGTMDataLayer)
}).views(self => {
    return {

        /* url of image is as expected, not to postpone load */
        get isFinalImgAssigned() {
            return self.imgUrl && self.imgUrl.indexOf("data:") === -1;
        }
    }
})
    .actions(self => {
        return {
            setImgUrlFromLoaded: () => {
                self.imgUrl = self.loadedImgUrl;
                self.imgWebPUrl = self.loadedImgWebPUrl;

                const imgSize = GetImageSize(true);
                self.imgWidth = imgSize.width;
                self.imgHeight = imgSize.height;
            },
            setIsFinalImgLoaded: () => {
                self.isFinalImgLoaded = true;
            },
            onClick: () => {
                if (self.gTMDataLayer?.viewPromotion) {
                    if (window["easitViewPromotionGTMDataLayer"]) {
                        window["easitViewPromotionGTMDataLayer"].select(self.gTMDataLayer.viewPromotion.type, self.index);
                    }
                    return;
                }
                if (self.gTMDataLayer?.viewItemList) {
                    if (window["easitViewItemListGTMDataLayer"]) {
                        window["easitViewItemListGTMDataLayer"].select(self.gTMDataLayer.viewItemList.type, self.index);
                    }
                    return;
                }
            }
        }
    }
    );

export const BannerStore = types
    .model({
        items: types.array(bannerItemModel),
        selectedIndex: types.optional(types.number, 0),
        repeatCnt: types.optional(types.number, 0),
        rotationIntervalInSec: types.optional(types.number, 8),
        isLoading: true,
        canShowRealImages: types.optional(types.boolean, false)
    })
    .views(self => ({
        get categories() {
            return self.items;
        },

        get selectedItem() {
            return self.items[self.selectedIndex];
        }
    }))
    .actions(self => {
        const env = getEnv<IAppEnv>(self);
        const gTMLayerDataType = "slider";

        const setSelected = (selectedIndex: number) => {
            let idx = selectedIndex;

            if (idx < 0) {
                idx = self.items.length - 1;
                self.repeatCnt -= 1;
            }
            if (idx > self.items.length - 1) {
                idx = 0;
                self.repeatCnt += 1;
            }

            self.selectedIndex = idx;
        };

        const createOddModel = (eventId: number, gameId: string, detailId: number, item: IOdds, isDetailSuspended: boolean | null, renameTipData: IRenameTipData | null) => {
            if (!item) {
                return null;
            }
            return OddModel.create(<SnapshotOut<typeof OddModel>>{
                name: getLongTipNameRaw(item.Name, renameTipData),
                rate: item.Rate,
                ident: `${eventId}-${gameId}-${detailId}_${item.TipID}_${item.Rate.toFixed(2)}-0`,
                key: `${eventId}-${gameId}-${detailId}_${item.TipID}`,
                isSuspended: isDetailSuspended || item.State === OfferItemState.Suspended
            });
        };

        let ctx: IGroupedApiCtx<ISportsBettingApi> | null = null;
        const loadAsync: () => Promise<void> = flow(function* () {
            try {

                self.isLoading = true; // always show isLoading. Or there is a problem with rerender as data before and after load are the same. And data in homeProxy is local.

                if (ctx && ctx.isOpen()) {
                    ctx.cancel('Load has been canceled.');
                }
                ctx = env.api.open();
                const r: ISBApiOperationResponse<IBannersList> = yield env.webHomeDataProxy.getBannersAsync();

                let gtmDataLayerViewPromotion: { items: { index: number }[] };
                let gtmDataLayerViewItemList: { items: { index: number }[] };
                if (r.ReturnValue.GTMDataLayer?.ViewPromotionAsJson) {
                    gtmDataLayerViewPromotion = JSON.parse(r.ReturnValue.GTMDataLayer.ViewPromotionAsJson);
                }
                if (r.ReturnValue.GTMDataLayer?.ViewItemListAsJson) {
                    gtmDataLayerViewItemList = JSON.parse(r.ReturnValue.GTMDataLayer.ViewItemListAsJson);
                }

                self.rotationIntervalInSec = r.ReturnValue.RotationIntervalInSec;
                self.items = observable(r.ReturnValue.Banners.map((item, idx): Instance<typeof bannerItemModel> => {

                    let game: IGame = null;
                    let detail: IDetail = null;
                    let odds: IOdds[] = null;
                    let isLiveAvailable: boolean;

                    if (!!item.Odds && !!item.Odds.Game) {

                        isLiveAvailable = item.Odds.IsLiveAvailable;
                        game = item.Odds.Game;
                        if (game.Details && game.Details.length > 0) {
                            detail = item.Odds.Game.Details[0];
                            odds = detail.OddsList;
                        }
                    } else {
                        isLiveAvailable = true; // not found in prematch. We also do not know a time.
                    }

                    let sourceViewPromotion: { index: number } = gtmDataLayerViewPromotion?.items.find(sourceItem => sourceItem.index === idx);
                    let sourceViewItemList: { index: number } = gtmDataLayerViewItemList?.items.find(sourceItem => sourceItem.index === idx);

                    if (item.FullWebPImgUrl === "") {
                        item.FullWebPImgUrl = null;
                    }

                    const useFullImage = !idx || self.canShowRealImages;
                    const imgSize = GetImageSize(useFullImage);

                    return bannerItemModel.create({
                        label: item.Title,
                        index: idx,
                        imgUrl: useFullImage ? item.FullImgUrl : "data:image/png;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=",
                        loadedImgUrl: item.FullImgUrl,
                        imgWebPUrl: useFullImage ? item.FullWebPImgUrl : null,
                        imgWidth: imgSize.width,
                        imgHeight: imgSize.height,
                        loadedImgWebPUrl: item.FullWebPImgUrl,
                        linkUrl: item.LinkUrl,
                        isFinalImgLoaded: false,
                        oddsEventId: item.Odds ? protoUtils.protoLongNumberToNumber(item.Odds.EventId) : null,
                        canBeLive: isLiveAvailable,
                        oddsExtData: item.Odds && game ? BannerOddsExtDataPartial.create({
                            eventName: item.Odds.EventName,
                            gameName: item.Odds.Game.Name,
                            eventDate: protoUtils.protoTimeStamp2Date(item.Odds.EventDate),
                            number: detail.LineNumber,
                            offerType: OfferType.Prematch
                        }) : null,
                        odds: game && detail && odds
                            ? observable(
                                odds.map(o =>
                                    createOddModel(protoUtils.protoLongNumberToNumber(item.Odds.EventId), game.ID, detail.ID, o, detail.Suspended,
                                        getRenameTipDataFromCompetitorNames(getCompetitorNamesFromEventName(item.Odds.EventName)))
                                )
                            )
                            : null,
                        gTMDataLayer: sourceViewPromotion || sourceViewItemList ? BannerItemGTMDataLayer.create({
                            viewPromotion: sourceViewPromotion ? BannerItemGTMDataLayerViewPromotion.create({
                                type: gTMLayerDataType,
                                index: sourceViewPromotion.index
                            }) : null,
                            viewItemList: sourceViewItemList ? BannerItemGTMDataLayerViewItemList.create({
                                type: gTMLayerDataType,
                                index: sourceViewItemList.index
                            }) : null
                        }) : null
                    });
                }));

                if (r.ReturnValue.GTMDataLayer) {
                    if (gtmDataLayerViewPromotion?.items.length) { // always not null due to protobuf
                        const easitViewPromotionGTMDataLayer = window["easitViewPromotionGTMDataLayer"];
                        if (easitViewPromotionGTMDataLayer) {
                            easitViewPromotionGTMDataLayer.addData(gTMLayerDataType, gtmDataLayerViewPromotion);
                            easitViewPromotionGTMDataLayer.selectType(gTMLayerDataType);
                        }
                    }
                    if (gtmDataLayerViewItemList?.items.length) { // always not null due to protobuf
                        const easitViewItemListGTMDataLayer = window["easitViewItemListGTMDataLayer"];
                        if (easitViewItemListGTMDataLayer) {
                            easitViewItemListGTMDataLayer.addData(gTMLayerDataType, gtmDataLayerViewItemList);
                        }
                    }
                }

                self.isLoading = false;
            } catch (error) {
                env.loggingService.error(error);
            } finally {
                ctx = null;
            }
        });

        const setCanShowRealImages = (canShowRealImages: boolean) => {
            if (self.canShowRealImages != canShowRealImages) {
                self.canShowRealImages = canShowRealImages;
                if (canShowRealImages) {
                    self.items.forEach((item) => {
                        item.setImgUrlFromLoaded();
                    })
                }
            }
        };

        const findInitialGameInEvent: (evt: LiveEvents.EventsItem | null) => LiveEvents.IGame = (evt: LiveEvents.EventsItem | null) => {
            let result: LiveEvents.IGame = null;  //same in desktop and mobile banner.ts
            evt?.GameGroups?.some(gg => {
                return gg.Games?.some(g => {
                    if (g.Initial) {
                        result = g;
                        return true;
                    }
                    return false;
                });
            });
            return result;
        }

        // //same filtering in desktop and mobile banner.ts
        const apiDateConverter = getSBApiStringToDateConverter(env);
        const processLiveEventChange: (eventId: number, eventData: LiveEvents.EventsItem) => void = (eventId: number, eventData: LiveEvents.EventsItem) => {
            self.items.filter(banner => banner.oddsEventId == eventId).forEach(banner => {

                var game = findInitialGameInEvent(eventData);
                if (game && game.Details?.length) {
                    let detail = game.Details[0];

                    banner.oddsExtData = BannerOddsExtDataPartial.create({
                        eventName: eventData.Name,
                        gameName: game.Name,
                        eventDate: apiDateConverter(eventData.Date),
                        number: detail.LineNumber,
                        offerType: OfferType.Live
                    });
                    const renameTipData = getRenameTipDataFromCompetitorNames(getCompetitorNamesFromEventName(eventData.Name));
                    banner.odds = observable(
                        detail.OddsList.map(o => createOddModel(eventId, game.ID, detail.ID, o, detail.Suspended, renameTipData))
                    )
                } else {
                    banner.oddsExtData = null;
                    banner.odds = null;
                }
            });
        }

        // //same in desktop and mobile banner.ts
        let subscribedEvents: number[] = null;
        const subscribeForLiveEventDetailsAsync: (eventChangeHandler: (data: { eventId: number, eventData: LiveEvents.EventsItem }) => void) => Promise<void> = flow(function* (eventChangeHandler: (data: { eventId: number, eventData: LiveEvents.EventsItem }) => void) {

            if (subscribedEvents) {
                throw new Error("Banner expected to be shown once (subscribedEvents is already initialized");
            }

            subscribedEvents = [];
            const promises: Promise<LiveEvents.EventsItem | null>[] = [];

            //subscribe not found in prematch only
            self.items.filter(banner => banner.oddsEventId && banner.canBeLive).forEach(banner => {
                env.sRNotificationHandler.openingEventDetail(banner.oddsEventId);
                const promise = env.sRNotificationHandler.subscribeForEventDetailAsync(banner.oddsEventId, eventChangeHandler);
                promise.then((data) => {
                    if (data) { // do not set if null (not found in live) as it may override data from prematch
                        eventChangeHandler({ eventId: banner.oddsEventId, eventData: data });
                    }
                });
                promises.push(promise);
                subscribedEvents.push(banner.oddsEventId);
            });
            if (promises.length) {
                yield Promise.all(promises);
            }
        });

        // //same in desktop and mobile banner.ts
        const unsubscribeForLiveEventDetailsAsync: (eventChangeHandler: (data: { eventId: number, eventData: LiveEvents.EventsItem }) => void) => Promise<void> = flow(function* (eventChangeHandler: (data: { eventId: number, eventData: LiveEvents.EventsItem }) => void) {
            if (!subscribedEvents) {
                return;
            }
            const promises: Promise<void>[] = [];
            subscribedEvents.forEach(eventId => {
                env.sRNotificationHandler.closingEventDetail(eventId);
                env.sRNotificationHandler.unregisterNotificationHandlerForEventDetail(eventId, eventChangeHandler);
                promises.push(env.sRNotificationHandler.unsubscribeFromEventDetailAsync(eventId));
            });   //TODO stop subscribing if unsubscribing (if still running)
            try {
                if (promises.length) {
                    yield Promise.all(promises);
                }
            }
            catch (e) {
                env.loggingService.error(e);
            }
            subscribedEvents = null;
        });

        return {
            setSelected,
            loadAsync,
            setCanShowRealImages,
            subscribeForLiveEventDetailsAsync,
            unsubscribeForLiveEventDetailsAsync,
            processLiveEventChange
        };
    });

const GetImageSize = (isFullImage: boolean): { width: number, height: number } => {
    if (isFullImage) {
        if (__IsMTSTipos__) {
            return { width: 798, height: 230 };
        }
        return { width: 1450, height: 244 };
    }
    return { width: 1, height: 1 };
}